<template>
  <div>
    <div v-if="!show2FAForm">
      <v-form id="login-form" ref="loginForm" lazy-validation>
        <v-container>
          <v-row>
            <v-col
                cols="12"
                offset-md="3"
                md="6"
            >
              <v-text-field
                  v-model="email"
                  :rules="emailRules"
                  :label="$vuetify.lang.t('$vuetify.auth.emailLabel')"
                  required
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col
                cols="12"
                offset-md="3"
                md="6"
            >
              <v-text-field
                  v-model="password"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :rules="passwordRules"
                  :label="$vuetify.lang.t('$vuetify.auth.passwordLabel')"
                  :type="showPassword ? 'text' : 'password'"
                  @click:append="showPassword = !showPassword"
                  required
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col
                cols="10"
                offset="1"
                offset-md="3"
                md="6"
            >
              <v-checkbox
                  v-model="rememberMe"
                  :label="$vuetify.lang.t('$vuetify.auth.rememberMeLabel')"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-row>
            <v-col
                cols="10"
                offset="1"
                offset-md="3"
                md="6"
            >
              <v-btn class="h-50" dark block color="primary" @click="login()">
                {{ $vuetify.lang.t('$vuetify.auth.logInBtn') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col
                cols="10"
                offset="1"
                offset-md="3"
                md="6"
                class="d-flex justify-center"
            >
              <v-btn text color="info" @click="registerNewAccount()" class="text-lowercase">
                {{ $vuetify.lang.t('$vuetify.auth.registerNewAccountBtn') }}
              </v-btn>
            </v-col>
          </v-row>
          <v-row class="mt-2">
            <v-col
                cols="10"
                offset="1"
                offset-md="3"
                md="6"
                class="d-flex justify-center pt-0 mt-0"
            >
              <v-btn text :href="forgotPasswordLink" class="text-lowercase text-muted" target="_blank">
                <span class="font-weight-light text--secondary">{{ $vuetify.lang.t('$vuetify.auth.forgotPassword') }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </div>
    <div v-if="show2FAForm">
      <v-form id="MFA-form" ref="mfaForm">
        <v-container>
          <v-row>
            <v-col
                cols="12"
                offset-md="3"
                md="6"
            >
              <v-text-field
                  v-model="mfaCode"
                  label="Enter your 2-factor code"
                  required
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col
                cols="10"
                offset="1"
                offset-md="3"
                md="6"
            >
              <v-btn class="h-50" block color="primary" :disabled="!mfaCodeFilled" @click="verifyTwoFactorChallenge()">
                {{ $vuetify.lang.t('$vuetify.auth.verifyMFABtn') }}
              </v-btn>
              <v-btn class="h-50 white--text" block color="red" @click="logOut()">
                {{ $vuetify.lang.t('$vuetify.auth.logOutBtn') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </div>
    <simple-popup :is-visible="showAuthError"
                  :title="$vuetify.lang.t('$vuetify.auth.errorPopupTitle')"
                  :body="authErrorMessage"
                  :custom_positive_btn_text="$vuetify.lang.t('$vuetify.global.close')"
                  :display_negative_btn="false"
                  @positive_btn_clicked="showAuthError = false"
    ></simple-popup>
    <already-authenticated-popup
        :is-visible="alreadyAuthenticatedUserResponse"
        :title="$vuetify.lang.t('$vuetify.auth.confirmIdentify')"
        :body="alreadyAuthenticatedMessageBody"
        :custom_positive_btn_text="$vuetify.lang.t('$vuetify.global.yes')"
        :custom_negative_btn_text="$vuetify.lang.t('$vuetify.auth.logOutBtn')"
        :display_negative_btn="true"
        @positive_btn_clicked="confirmUser()"
        @negative_btn_clicked="logOut()"
    >

    </already-authenticated-popup>
  </div>

</template>

<script>
import SimplePopupComponent from "@/components/modules/global/simplePopup/SimplePopupComponent";
import appSettings from "@/mixins/config/appSettings";

export default {
  name: "loginForm",
  components: {
    simplePopup: SimplePopupComponent,
    alreadyAuthenticatedPopup : SimplePopupComponent,
  },
  data: () => {
    return {
      showPassword: false,
      email: null,
      password: null,
      rememberMe: false,
      mfaCode: null,
      alreadyAuthenticatedUserResponse: null,
      alreadyAuthenticatedMessageBody: '',
      show2FAForm: false,
      showAuthError: false,
      authErrorMessage: '',

    }
  },
  methods: {
    async login() {
      if (this.$refs.loginForm.validate()) {
        let response = await this.$auth.login({
          email: this.email,
          password: this.password,
          remember: (this.rememberMe) ? true : false
        }, false);

        if (!this.$auth.error) {
          if (response.status == 218 && response.data.status == 'already-authenticated' && response.data.user) {
            this.alreadyAuthenticatedMessageBody = (response.data.user.email)? response.data.user.email : ((response.data.user.intials)? response.data.user.intials: 'ohoo');
            this.alreadyAuthenticatedUserResponse = response;
          } else {
            if (response.data.two_factor === true) {
              this.show2FAForm = true;
            } else {
              await this.verifyAuthenticatedUserResponse(response);
            }
          }
        } else {
          this.displayAuthenticationError(this.processLoginError(this.$auth.error));
          this.logOut();
        }
      }
    },
    async verifyTwoFactorChallenge() {
      if (this.$refs.mfaForm.validate()) {
        let response = await this.$auth.sendTwoFactorChallenge(this.mfaCode);
        if (!this.$auth.error) {
          await this.verifyAuthenticatedUserResponse(response);
        } else {
          this.displayAuthenticationError(this.processLoginError(this.$auth.error));
          this.logOut();
        }
      }
    },
    async verifyAuthenticatedUserResponse(response) {
      if (response.data.user && response.data.user.email) {
        //PREFIRE SESSION LOADER TO COUNTER DISK USAGE /TIME
        this.$workSessions.loading = true
        this.$emit('userLoggedIn', true);
        await this.$auth.setAuthenticatedAndContinue(response.data.user);
        //COLLECT WORKSESSION INFO!!
        await this.$workSessions.checkUserHasOpenWorkSession();

      } else {
        this.displayAuthenticationError("Could not retrieve user data...");
        this.logOut();
      }
    },
    async confirmUser() {
      if (this.alreadyAuthenticatedUserResponse) {
        await this.verifyAuthenticatedUserResponse(this.alreadyAuthenticatedUserResponse)
      }
    },
    async logOut() {
      this.clearAllFormData();
      await this.$auth.logout()
    },
    clearAllFormData() {
      this.email = null;
      this.password = null
      this.show2FAForm = false;
      this.mfaCode = null;
      this.alreadyAuthenticatedUserResponse = null
    },
    processLoginError($error) {
      var message = this.$vuetify.lang.t('$vuetify.auth.errors.unknownError');
      if ($error.response.data && $error.response.data.message) {
        message = $error.response.data.message
      } else {
        if ($error.message) {
          message = $error.message
        }
      }
      return message;
    },
    displayAuthenticationError(message) {
      this.authErrorMessage = message;
      this.showAuthError = true;
    },
    registerNewAccount(){
        let reglink = appSettings.data().appSettings.PLATFORM_BACKEND_BASE_URL+'/register?redirect='+window.location.origin+'&lang='+this.$store.getters.getUserLanguage;
        window.location.href = reglink;
    }
  },
  computed: {
    forgotPasswordLink : function (){
      return appSettings.data().appSettings.PLATFORM_BACKEND_BASE_URL+'/forgot-password';
    },
    mfaCodeFilled: function () {
      if (this.mfaCode == null || this.mfaCode.length <= 0) {
        return false;
      } else {
        return true
      }
    },
    emailRules: function () {
      return [
        value => !!value || this.$vuetify.lang.t('$vuetify.auth.errors.required'),
        value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || this.$vuetify.lang.t('$vuetify.auth.errors.invalidEmail')
        }
      ]
    },
    passwordRules: function () {
      return [
        value => !!value || this.$vuetify.lang.t('$vuetify.auth.errors.required')
      ]
    }
  },
  created() {
    window.console.log('LOGIN WINDOW CREATED');
  }
}
</script>

<style scoped>

</style>